import { useMemo } from "react";
import { Stack } from "@volca/ui/components/ui/stack";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@volca/ui/components/ui/tabs";
import { cn } from "@volca/ui/lib/utils";
import { useComparison } from "./comparison/comparison-query";
import { ComparisonView } from "./comparison/comparison-view";
import { ProposalMenu } from "./proposal-menu";
import { PublishProposal } from "./publish-proposal";
import {
  useEditorAccessKeys,
  useEditorRoster,
  type PayrollEditorQueries,
} from "./query";
import { RulesAccess } from "./rules-access";
import { RulesChanges } from "./rules-changes";
import { rulesDocumentDiff } from "./rules-diff";
import { RulesRoster } from "./rules-roster";
import { comparisonImpossible } from "./state";
import type { Workspace } from "./types";

/**
 * One mark, two states, on every tab that can carry it.
 *
 * Orange: there is something new on this tab, and it is ready to read. Muted
 * and pulsing: work that will land on this tab is still running. Nothing: this
 * tab holds what it held before the proposal.
 *
 * Orange rather than green. Green is this app's primary-action colour and it
 * reads as "all clear", which is the wrong thing to say about a change to how
 * people are paid. The mark asks for attention; it does not give approval.
 *
 * Only the Pay tab can be "working", because the side-by-side is the only part
 * that takes minutes. The rules and the roster move inside the turn, so by the
 * time there is anything to say about them it is already there to read.
 */
// The artifact. The product's own white, running flush to every edge of its
// column: an inset card with rounded corners spent space this screen does not
// have, and read as a widget rather than as the document being worked on. What
// separates it from the chrome is the control rows above it, not a margin.
const SHEET = "min-h-0 flex-1 overflow-hidden bg-surface-raised ";

const TabMark = ({ state }: { state: "ready" | "working" | "none" }) => {
  if (state === "none") return null;
  return (
    <>
      <span
        aria-hidden
        className={cn(
          "ml-1.5 inline-block h-1.5 w-1.5 rounded-full align-middle",
          {
            "bg-accent-orange-bold": state === "ready",
            "animate-pulse bg-neutral": !(state === "ready"),
          },
        )}
      />
      <span className="sr-only">
        {state === "ready" ? ": ready to read" : ": still working"}
      </span>
    </>
  );
};

/** What a workspace with no proposal has: nothing to show and nothing to mark. */
const NO_DIFF: ReturnType<typeof rulesDocumentDiff> = {
  blocks: [],
  changes: 0,
  comparable: true,
  added: 0,
  removed: 0,
};

export const RulesReview = ({
  workspace,
  businessId,
  workspaceId,
  tab,
  onTabChange,
  periodStart,
  onPeriodChange,
  publish,
  discard,
  busy,
}: {
  workspace: Workspace;
  businessId: string;
  workspaceId: string;
  tab: string;
  onTabChange: (tab: string) => void;
  periodStart: string | undefined;
  onPeriodChange: (start: string | undefined) => void;
  publish: PayrollEditorQueries["publish"];
  discard: PayrollEditorQueries["discard"];
  busy: boolean;
}) => {
  const roster = useEditorRoster(
    businessId,
    workspaceId,
    !!workspace.active_request,
  );
  const active = !!workspace.active_request;
  const comparison = useComparison(
    businessId,
    workspaceId,
    periodStart,
    true,
    active,
  );
  const access = useEditorAccessKeys(
    businessId,
    workspaceId,
    !!workspace.active_request,
  );
  // Computed here rather than inside the tab, so the Rules mark and the diff
  // itself read the same number from one pass over the document.
  //
  // No proposal means no diff. Comparing the current rules against nothing
  // called every line of them removed, so the Rules tab wore a change mark on
  // a workspace where nothing had been proposed, and walking two thousand
  // lines to reach that answer.
  const currentRules = workspace.current?.rules;
  const proposal = workspace.proposed;
  const diff = useMemo(
    () =>
      proposal ? rulesDocumentDiff(currentRules, proposal.rules) : NO_DIFF,
    [currentRules, proposal],
  );
  // Widening is a property of this pane, not of one tab inside it. It used to
  // belong to the comparison alone, which meant the rules and the roster — a
  // long document and a wide table — had no way to get the window, and the one
  // control that did it was buried in the comparison's own toolbar. The tabs
  // stay put; the pane takes the width and the conversation becomes a rail.
  return (
    <section
      className="flex min-h-0 min-w-0 flex-col"
      aria-label="Payroll rules review"
    >
      <Tabs
        value={tab}
        onValueChange={onTabChange}
        className="flex min-h-0 flex-1 flex-col gap-0"
      >
        {/* The design system's own tabs, with their underline. This used to be
            a plain TabsList wearing a grey pill on the active tab, which is the
            preset default rather than anything Volca uses. The controls sit
            beside the list rather than inside it, because a tablist holds tabs
            and a Publish button is not one. */}
        {/* No bottom border: the tab strip already draws its own underline
            under the active tab, and the step from this white band to the grey
            control row beneath says where one ends and the other begins.

            The `pb-px` is for that underline. The design system draws it one
            pixel below the list so it lands on the bottom border the component
            normally has, and with that border gone it hung into the grey row
            instead. The padding gives it the pixel back, so it rests on the
            boundary rather than straddling it. */}
        <Stack
          gap={0}
          align="stretch"
          direction="row"
          wrap
          className="shrink-0 items-center gap-x-2 bg-surface-raised pr-3 pb-px"
        >
          {/* `h-10` over the component's own `h-8`: this strip sits directly
              under the app's nav bar, and the underline needs room under the
              labels. */}
          <TabsList
            variant="line"
            className="group-data-horizontal/tabs:h-10"
            aria-label="Rules"
          >
            {/* Three tabs, because an edit can move three things: what the rules
              say, what the pay does, and who the rules are about. Current rules
              and Proposed rules were separate tabs showing the two sides of a
              diff this view already renders in one place. */}
            <TabsTrigger value="changes">
              Rules
              <TabMark state={diff.changes ? "ready" : "none"} />
            </TabsTrigger>
            {/* The reply no longer links here, so this mark is the invitation. */}
            <TabsTrigger value="compare">
              Pay
              <TabMark
                // Running wins over ready. A proposal that has a finished
                // comparison AND a new one in flight was marked "ready",
                // which pointed the reader at numbers that were about to be
                // replaced.
                state={
                  comparison.state.data?.running
                    ? "working"
                    : comparison.state.data?.ready
                      ? "ready"
                      : "none"
                }
              />
            </TabsTrigger>
            {/* The third thing an edit can move. The rules say how people are
              paid, the comparison says what they are paid, and the roster says
              who they are and what the rules read about them — which is where a
              one-person rate belongs, and where the assistant puts it. */}
            {roster.data?.present !== false && (
              <TabsTrigger value="roster">
                Roster
                <TabMark
                  state={roster.data?.changed_worker_count ? "ready" : "none"}
                />
              </TabsTrigger>
            )}
            {/* Who can read what. It is a tab rather than a warning because
                the gate that used to refuse these changes is gone: no new
                sheet could ever pass it, so the question it asked is answered
                here instead, where a person can act on it. */}
            {access.data?.present !== false && (
              <TabsTrigger value="access">
                Access
                <TabMark
                  state={
                    // Counted off the rows, so a state added to the contract
                    // cannot be forgotten here. Summing the server's per-state
                    // lists missed `reassigned` the day it arrived.
                    access.data?.keys.some(
                      (entry) => entry.state !== "unchanged",
                    )
                      ? "ready"
                      : "none"
                  }
                />
              </TabsTrigger>
            )}
          </TabsList>
          {/* The page used to carry a title, a sentence about publishing and
              a state label, in a band of its own above the split. The sentence
              is already the first thing the publish dialog says, the title
              named a page the reader had just navigated to, and the label said
              in the vaguest words what the conversation beside it shows as it
              happens. Publishing is the only one of them that was a control. */}
          <div className="ml-auto" />
          <PublishProposal
            workspace={workspace}
            publish={publish}
            comparisonReady={comparison.state.data?.ready ?? false}
            // Only a refusal that says a comparison cannot run at all. A
            // network or parsing failure is not the same answer, and treating
            // it as one dropped the gate and let a change publish unchecked.
            comparisonPossible={!comparisonImpossible(comparison.state.error)}
            busy={busy}
          />
          <ProposalMenu workspace={workspace} discard={discard} busy={busy} />
        </Stack>
        <TabsContent
          value="changes"
          className={cn(SHEET, "flex-col not-data-hidden:flex")}
        >
          <RulesChanges workspace={workspace} diff={diff} />
        </TabsContent>
        {/* The roster owns its own scrolling, so its control row can stay put
            while the table moves under it. */}
        <TabsContent
          value="roster"
          className={cn(SHEET, "flex-col not-data-hidden:flex")}
        >
          <RulesRoster
            businessId={businessId}
            workspaceId={workspaceId}
            active={!!workspace.active_request}
          />
        </TabsContent>
        <TabsContent
          value="access"
          className={cn(SHEET, "flex-col not-data-hidden:flex")}
        >
          <RulesAccess
            businessId={businessId}
            workspaceId={workspaceId}
            active={!!workspace.active_request}
          />
        </TabsContent>
        <TabsContent
          value="compare"
          // `display` from a class beats the browser's `[hidden]` rule, so an
          // always-flex panel keeps its box while inactive and steals half the
          // height from whichever tab is actually open.
          className={cn(SHEET, "flex-col not-data-hidden:flex")}
        >
          <ComparisonView
            businessId={businessId}
            workspaceId={workspaceId}
            editable={workspace.editable}
            payMayMove={workspace.pay_may_move}
            active={active}
            periodStart={periodStart}
            onPeriodChange={onPeriodChange}
          />
        </TabsContent>
      </Tabs>
    </section>
  );
};
